import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { ArtistSlideshow } from "./ArtistSlideshow";

export const Hero = () => {
    return (
        <section
            id="top"
            className="relative min-h-screen flex items-end md:items-center overflow-hidden pt-28 pb-10 md:pb-20"
            data-testid="hero-section"
        >
            {/* Ambient glow blobs */}
            <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#791EE2] opacity-25 blur-[140px] pulse-glow pointer-events-none" />
            <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#F54815] opacity-15 blur-[140px] pulse-glow pointer-events-none" />

            {/* Artist slideshow background */}
            <div className="absolute inset-0 z-0">
                <ArtistSlideshow />
            </div>

            {/* Desktop left-to-right dark cinematic gradient */}
            <div
                className="absolute inset-0 z-[1] pointer-events-none hidden md:block"
                style={{
                    background:
                        "linear-gradient(90deg, #030023 0%, rgba(3,0,35,0.92) 20%, rgba(3,0,35,0.7) 42%, rgba(3,0,35,0.25) 68%, rgba(3,0,35,0.1) 100%)",
                }}
            />
            {/* Mobile vertical fade — transparent top (artist visible) → dark bottom (text readable) */}
            <div
                className="absolute inset-0 z-[1] pointer-events-none md:hidden"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(3,0,35,0.1) 0%, rgba(3,0,35,0.15) 32%, rgba(3,0,35,0.75) 55%, rgba(3,0,35,0.95) 72%, #030023 100%)",
                }}
            />
            {/* Bottom fade into next section */}
            <div className="absolute inset-x-0 bottom-0 h-24 md:h-40 z-[1] pointer-events-none bg-gradient-to-b from-transparent to-[#030023]" />

            <div className="relative z-10 w-full px-6 md:px-[4.5vw]">
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.12 } },
                    }}
                    className="w-full md:max-w-[62%] lg:max-w-[58%] xl:max-w-[54%]"
                >
                    <motion.div
                        variants={fadeUp}
                        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md mb-6 md:mb-8"
                        data-testid="hero-badge"
                    >
                        <span className="w-2 h-2 rounded-full bg-[#F54815] animate-pulse" />
                        <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/80 font-medium">
                            A global music platform
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={fadeUp}
                        className="font-heading text-[30px] leading-[1.08] sm:text-5xl md:leading-[0.95] lg:text-6xl xl:text-[72px] 2xl:text-[80px] font-extrabold tracking-tighter text-white mb-3 md:mb-7"
                        data-testid="hero-headline"
                    >
                        <span className="md:hidden">
                            Your Music,{" "}
                            <span className="gradient-text">Your Future</span>
                        </span>
                        <span className="hidden md:inline">
                            Your Music,
                            <br />
                            <span className="gradient-text">Your Future</span>
                        </span>
                    </motion.h1>

                    <motion.p
                        variants={fadeUp}
                        className="text-[12px] leading-[1.5] md:text-xl md:leading-relaxed text-white/70 max-w-md md:max-w-2xl mb-5 md:mb-12 font-body"
                        data-testid="hero-subtext"
                    >
                        Maajja is where music, technology, and culture come
                        together to build the future of independent artists.
                        Own your sound, scale your reach, keep your rights.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        className="flex flex-row sm:flex-row gap-3 md:gap-4"
                    >
                        <a
                            href="#cta"
                            onClick={(e) => {
                                if (window.innerWidth < 768) {
                                    e.preventDefault();
                                    window.dispatchEvent(new CustomEvent("maajja:join"));
                                }
                            }}
                            className="group flex-1 md:flex-initial md:w-auto inline-flex items-center justify-center gap-2 bg-[#791EE2] hover:bg-[#8b32f0] text-white py-3 md:px-8 md:py-4 rounded-full font-semibold text-[13px] md:text-base transition-all hover:shadow-[0_0_50px_rgba(121,30,226,0.6)] active:scale-95"
                            data-testid="hero-cta-join"
                        >
                            Join Maajja
                            <ArrowRight
                                size={14}
                                className="transition-transform group-hover:translate-x-1 md:w-[18px] md:h-[18px]"
                            />
                        </a>
                        <a
                            href="#what"
                            className="group flex-1 md:flex-initial md:w-auto inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 hover:bg-white/5 text-white py-3 md:px-8 md:py-4 rounded-full font-semibold text-[13px] md:text-base transition-all backdrop-blur-md active:scale-95"
                            data-testid="hero-cta-explore"
                        >
                            <Play
                                size={12}
                                className="fill-white md:w-4 md:h-4"
                            />
                            Explore
                            <span className="hidden sm:inline"> platform</span>
                        </a>
                    </motion.div>

                    {/* Stats strip */}
                    <motion.div
                        variants={fadeUp}
                        className="mt-6 md:mt-20 grid grid-cols-3 gap-4 md:gap-8 max-w-2xl border-t border-white/10 pt-4 md:pt-10"
                        data-testid="hero-stats"
                    >
                        {[
                            { val: "150+", label: "Countries reached" },
                            { val: "10M+", label: "Streams daily" },
                            { val: "100%", label: "Artists own rights" },
                        ].map((s) => (
                            <div key={s.label}>
                                <div className="font-heading text-[24px] md:text-3xl lg:text-4xl font-extrabold text-white leading-none">
                                    {s.val}
                                </div>
                                <div className="text-[10px] md:text-xs lg:text-sm text-white/55 mt-1 leading-tight">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
